import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import momentTZ from "moment-timezone";

import Hr from "components/atoms/Hr/Hr";
import { useGetAddressesV2Details } from "api/queries/company/useGetAddressV2Details";
import { StopType } from "types/MultiStop";

export default function MultiDropSchedule() {
  const { getValues } = useFormContext();

  const stops = getValues("schedule.stops");

  return (
    <>
      {stops?.map((stop: any, index: number) => (
        <DropSchedule key={index} stop={stop} index={index} />
      ))}
    </>
  );
}

function DropSchedule({ stop, index }: any) {
  const { getValues } = useFormContext();
  const stops = getValues("schedule.stops");
  const {
    address_name,
    address_reference_number,
    stop_after_date,
    stop_after_time,
    stop_before_date,
    stop_before_time,
    stop_contact_email,
    stop_contact_name,
    stop_contact_number,
    stop_notes,
    stop_type,
    timezone,
    stop_hours_start,
    stop_hours_end,
  } = stop ?? {};

  const { data: addressV2Details } = useGetAddressesV2Details(address_reference_number, {
    options: {
      enabled: Boolean(address_reference_number),
    },
  });
  const addressStreetAddress = addressV2Details?.street_address;
  const addressCityAndPostal = `${addressV2Details?.city_name}${addressV2Details?.zipcode && ", "}${
    addressV2Details?.zipcode
  }`;
  const addressStateAndCountry = `${addressV2Details?.state_name}, ${addressV2Details?.country_name}`;

  const afterDateTime = `${stop_after_date}T${stop_after_time}`;
  const formattedAfterDateTime = momentTZ.tz(afterDateTime, timezone).format("DD MMM YYYY, HH:mm [UTC] Z");

  const beforeDateTime = stop_before_date && stop_before_time ? `${stop_before_date}T${stop_before_time}` : null;
  const formattedBeforeDateTime = beforeDateTime
    ? momentTZ.tz(beforeDateTime, timezone).format("DD MMM YYYY, HH:mm [UTC] Z")
    : null;

  const formattedDateTime = (
    <>
      <div>{formattedAfterDateTime}</div>
      {formattedBeforeDateTime && (
        <>
          <div>
            <FormattedMessage id="general.to" defaultMessage="To" />
          </div>
          <div>{formattedBeforeDateTime}</div>
        </>
      )}
    </>
  );

  const specifiedHours =
    stop_hours_start && stop_hours_end
      ? `${moment(stop_hours_start, "HH:mm").format("HH:mm")} - ${moment(stop_hours_end, "HH:mm").format("HH:mm")}`
      : "";

  const data = [
    {
      key: "scheduled-time",
      label: <FormattedMessage id="new-request.schedule.scheduledTime" defaultMessage="Scheduled time" />,
      value: formattedDateTime,
    },
    {
      key: "company",
      label: <FormattedMessage id="new-request.quote-confirmation.schedule.company" defaultMessage="Company" />,
      value: address_name || "",
    },
    {
      key: "address",
      label: <FormattedMessage id="new-request.schedule.address" defaultMessage="Address" />,
      value: addressStreetAddress,
    },
    {
      key: "city-and-postal-code",
      label: (
        <FormattedMessage
          id="new-request.quote-confirmation.schedule.cityAndPostalCode"
          defaultMessage="City and postal code"
        />
      ),
      value: addressCityAndPostal,
    },
    {
      key: "state-and-country",
      label: (
        <FormattedMessage id="new-request.quote-confirmation.schedule.stateAndCountry" defaultMessage="State and country" />
      ),
      value: addressStateAndCountry,
    },
    {
      key: "contact-name",
      label: <FormattedMessage id="new-request.quote-confirmation.schedule.contactName" defaultMessage="Contact name" />,
      value: stop_contact_name || "",
    },
    {
      key: "contact-email",
      label: <FormattedMessage id="new-request.quote-confirmation.schedule.contactEmail" defaultMessage="Contact email" />,
      value: stop_contact_email || "",
    },
    {
      key: "contact-phone",
      label: <FormattedMessage id="new-request.quote-confirmation.schedule.contactPhone" defaultMessage="Contact phone" />,
      value: stop_contact_number || "",
    },
    ...(specifiedHours
      ? [
          {
            key: "specified-hours",
            label:
              stop_type === StopType.PICKUP ? (
                <FormattedMessage
                  id="new-request.quote-confirmation.schedule.specifyPickupHour"
                  defaultMessage="Specify pickup hour"
                />
              ) : (
                <FormattedMessage
                  id="new-request.quote-confirmation.schedule.specifyDeliveryHour"
                  defaultMessage="Specify delivery hour"
                />
              ),
            value: specifiedHours,
          },
        ]
      : []),
    ...(stop_notes
      ? [
          {
            key: "stop-notes",
            label:
              stop_type === StopType.PICKUP ? (
                <FormattedMessage id="quote-request-detail.pickupNotes" defaultMessage="Pickup notes" />
              ) : (
                <FormattedMessage id="quote-request-detail.deliveryNotes" defaultMessage="Delivery notes" />
              ),
            value: stop_notes || "",
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="text-secondary-v2 text-xs mb-lg">
        {index === 0 ? (
          <FormattedMessage id="general.pickup" defaultMessage="Pickup" />
        ) : (
          <FormattedMessage
            id="Delivery stop #{stopNumber}"
            defaultMessage="Delivery stop #{stopNumber}"
            values={{ stopNumber: index }}
          />
        )}
      </div>
      {data.map((item) => (
        <Row className="font-b3 mb-2" key={item.key}>
          <Col className="text-secondary">{item.label}</Col>
          <Col>{item.value}</Col>
        </Row>
      ))}
      {index !== stops.length - 1 && <Hr />}
    </>
  );
}
